import {Button, Modal, Select} from 'antd';
import {ReactElement, useMemo, useState} from 'react';

import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {LocalePlural} from '../../../../../../../../provider/locale/locale-plural';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {useModal} from '../../../../../../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {ReviewPresenceActionEnum} from '../../../../../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {useReviewPresenceActivity} from '../../../../../../../../service/reviews/review-realtime-presence/use-review-presence-activity-hook';
import {
    ReviewStatusType,
    updateReviewStatus,
    useReviewStatusList,
} from '../../../../../../../../service/reviews/reviews-statuses';
import {ReviewDataType} from '../../../../../../../../service/reviews/reviews-type';
import {handleFilterOption} from '../../../../../../../../util/antd/select-helper';
import {ReviewDataWithCatalogType} from '../../../review-list-type';
import {processBatch} from '../../review-helper';

import * as styles from './status-change-modal.scss';

const {Option} = Select;

type PropsType = {
    onCancel: () => void;
    onSuccess?: () => void;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    selectedReviews: Array<ReviewDataWithCatalogType>;
};

export function StatusChangeModal(props: PropsType): ReactElement {
    const {onCancel, onSuccess, updateReview, selectedReviews} = props;
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const {getLocalizedString, localeName} = useLocale();
    const {snackbar} = useSnackbar();
    const {modal} = useModal();

    const {data: reviewStatusList, isFetching: isReviewStatusListInProgress} = useReviewStatusList(localeName);

    const reviewsEligibleForStatusChange = useMemo(() => {
        const statuses = reviewStatusList?.statuses;

        if (!statuses) {
            return selectedReviews;
        }

        return selectedReviews.filter((review) =>
            statuses.some((statusItem) => statusItem.statusCode === review.processStatus)
        );
    }, [selectedReviews, reviewStatusList?.statuses]);

    const presenceActivities = useMemo(
        () =>
            selectedReviews.map((review) => ({
                reviewId: review.id,
                brandId: review.brand.id,
                action: ReviewPresenceActionEnum.Status,
            })),
        [selectedReviews]
    );

    useReviewPresenceActivity(presenceActivities, true);

    async function applyStatusChange(reviews: Array<ReviewDataWithCatalogType>, status: string): Promise<void> {
        if (reviews.length === 0) {
            return;
        }

        setIsLoading(true);

        try {
            await processBatch(reviews, 50, async (review) => {
                await updateReviewStatus(review.id, {status});
                updateReview(review.id, {processStatus: status});
            });

            snackbar.success(<Locale stringKey="REVIEWS__STATUS_CHANGE_SUCCESS" />);

            if (onSuccess) {
                onSuccess();
            } else {
                onCancel();
            }
        } catch {
            snackbar.error(<Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleOk() {
        if (!selectedStatus) {
            return;
        }

        const eligible = reviewsEligibleForStatusChange;
        const totalCount = selectedReviews.length;
        const canChangeCount = eligible.length;
        const canChangeAll = canChangeCount === totalCount;

        if (eligible.length === 0) {
            return;
        }

        if (!canChangeAll) {
            modal.confirm({
                maskClosable: true,
                title: <Locale stringKey="REVIEWS__BULK_STATUS__CONFIRM_TITLE" />,
                content: (
                    <div>
                        <LocalePlural
                            count={totalCount}
                            fewKey="REVIEWS__BULK_STATUS__CONFIRM_CONTENT__FEW"
                            manyKey="REVIEWS__BULK_STATUS__CONFIRM_CONTENT__MANY"
                            singularKey="REVIEWS__BULK_STATUS__CONFIRM_CONTENT__SINGULAR"
                            valueMap={{
                                canChangeCount: String(canChangeCount),
                                totalCount: String(totalCount),
                            }}
                        />
                    </div>
                ),
                cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
                okText: <Locale stringKey="REVIEWS__BULK_STATUS__CONFIRM_OK" />,
                onOk: async () => {
                    await applyStatusChange(eligible, selectedStatus);
                },
            });

            return;
        }

        await applyStatusChange(selectedReviews, selectedStatus);
    }

    return (
        <Modal
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Button disabled={!selectedStatus} key="ok" loading={isLoading} onClick={handleOk} type="primary">
                    <Locale stringKey="TEXT__APPLY" />
                </Button>,
            ]}
            onCancel={onCancel}
            open
            title={<Locale stringKey="REVIEWS__FILTER__SELECT_A_STATUS" />}
        >
            <Select
                className={styles.StatusChangeModal_select}
                filterOption={handleFilterOption}
                loading={isReviewStatusListInProgress}
                onChange={setSelectedStatus}
                optionFilterProp="children"
                placeholder={getLocalizedString('REVIEWS__FILTER__SELECT_A_STATUS')}
                showSearch
                value={selectedStatus}
            >
                {reviewStatusList?.statuses.map((reviewStatus: ReviewStatusType) => (
                    <Option key={reviewStatus.statusCode} value={reviewStatus.statusCode}>
                        {reviewStatus.title}
                    </Option>
                ))}
            </Select>
        </Modal>
    );
}
